import React, { useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { ArrowLeft, ShieldCheck, Send } from "lucide-react";

const REQUEST_TYPES = [
  { value: "deletion",         label: "Delete my data (Right to erasure)" },
  { value: "access",           label: "Access / export my data (Right of access)" },
  { value: "correction",       label: "Correct my data (Rectification)" },
  { value: "opt_out_sale",     label: "Opt out of data sale (CCPA)" },
  { value: "opt_out_marketing", label: "Opt out of marketing communications" },
  { value: "other",            label: "Other / Question" },
];

const REGIONS = [
  { value: "",   label: "Select your region (optional)" },
  { value: "JP", label: "Japan (APPI)" },
  { value: "EU", label: "European Union (GDPR)" },
  { value: "UK", label: "United Kingdom (UK GDPR)" },
  { value: "US", label: "United States (CCPA / state laws)" },
  { value: "CN", label: "China (PIPL)" },
  { value: "SG", label: "Singapore (PDPA)" },
  { value: "ID", label: "Indonesia (UU PDP)" },
  { value: "OTHER", label: "Other" },
];

export default function DataRequest() {
  const [form, setForm] = useState({
    type: "deletion",
    email: "",
    name: "",
    region: "",
    message: "",
    consent_confirmed: false,
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(null);

  const set = (k) => (e) => {
    const v = e.target.type === "checkbox" ? e.target.checked : e.target.value;
    setForm((f) => ({ ...f, [k]: v }));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.consent_confirmed) {
      toast.error("Please confirm the identity / consent checkbox");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post("/data-requests", {
        type: form.type,
        email: form.email,
        name: form.name || null,
        region: form.region || null,
        message: form.message || null,
        consent_confirmed: true,
      });
      setSubmitted(data);
      toast.success("Request submitted");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="mx-auto max-w-2xl px-6 py-20">
        <div className="border border-[#00FF94]/40 bg-[#00FF94]/5 p-8 rounded-md">
          <div className="h-12 w-12 bg-[#00FF94]/20 text-[#00FF94] flex items-center justify-center rounded-md mb-5">
            <ShieldCheck className="h-6 w-6" strokeWidth={1.75} />
          </div>
          <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#64748B] mb-2">
            // REQUEST RECEIVED · ID {submitted.id?.slice(0, 8)}
          </div>
          <h1
            className="font-display text-3xl tracking-tight text-white mb-3"
            style={{ fontWeight: 800 }}
            data-testid="data-request-success-title"
          >
            Request received
          </h1>
          <p className="text-sm text-[#94A3B8] leading-relaxed">
            {submitted.message_for_user}
          </p>
          <div className="mt-6 grid grid-cols-2 gap-3 text-xs">
            <div className="border border-[#1E2A3F] bg-[#0F1525] p-3">
              <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#64748B]">Type</div>
              <div className="text-white mt-1">{submitted.type}</div>
            </div>
            <div className="border border-[#1E2A3F] bg-[#0F1525] p-3">
              <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#64748B]">Status</div>
              <div className="text-[#3B82F6] mt-1">{submitted.status}</div>
            </div>
          </div>
          <div className="mt-8 flex gap-3">
            <Link
              to="/"
              data-testid="data-request-back-home"
              className="px-4 py-2.5 border border-[#1E2A3F] text-white font-mono text-[10px] uppercase tracking-[0.2em] hover:border-[#3B82F6] hover:text-[#3B82F6]"
            >
              ← Back to home
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-3xl px-6 lg:px-10 py-12 lg:py-20">
      <Link
        to="/"
        data-testid="data-request-back-link"
        className="inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.2em] text-[#94A3B8] hover:text-white mb-10"
      >
        <ArrowLeft className="h-3 w-3" strokeWidth={2} />
        Back
      </Link>

      <div className="mb-10">
        <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#64748B] mb-3">
          // DATA SUBJECT REQUEST · GDPR / CCPA / APPI
        </div>
        <h1 className="font-display text-4xl lg:text-5xl tracking-tight text-white" style={{ fontWeight: 800 }}>
          Data Request / Opt-out
        </h1>
        <p className="text-sm text-[#94A3B8] mt-4 leading-relaxed max-w-2xl">
          You have the right to access, correct, or delete personal data we hold about you,
          and to opt out of data sales or marketing. Submit your request below — we will
          respond within 30 days as required by applicable law.
        </p>
      </div>

      <form onSubmit={submit} className="space-y-6" data-testid="data-request-form">
        <div>
          <label className="block label-mono mb-2">Request type *</label>
          <select
            value={form.type}
            onChange={set("type")}
            data-testid="data-request-type-select"
            className="w-full bg-[#0A0E1A] border border-[#1E2A3F] focus:border-[#3B82F6] focus:ring-1 focus:ring-[#3B82F6] outline-none p-3 text-base text-white font-mono"
          >
            {REQUEST_TYPES.map((t) => (
              <option key={t.value} value={t.value}>{t.label}</option>
            ))}
          </select>
        </div>

        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <label className="block label-mono mb-2">Email *</label>
            <input
              type="email"
              required
              value={form.email}
              onChange={set("email")}
              data-testid="data-request-email-input"
              placeholder="you@example.com"
              className="w-full bg-[#0A0E1A] border border-[#1E2A3F] focus:border-[#3B82F6] focus:ring-1 focus:ring-[#3B82F6] outline-none p-3 text-base text-white font-mono"
            />
          </div>
          <div>
            <label className="block label-mono mb-2">Name (optional)</label>
            <input
              type="text"
              value={form.name}
              onChange={set("name")}
              data-testid="data-request-name-input"
              className="w-full bg-[#0A0E1A] border border-[#1E2A3F] focus:border-[#3B82F6] focus:ring-1 focus:ring-[#3B82F6] outline-none p-3 text-base text-white font-mono"
            />
          </div>
        </div>

        <div>
          <label className="block label-mono mb-2">Region</label>
          <select
            value={form.region}
            onChange={set("region")}
            data-testid="data-request-region-select"
            className="w-full bg-[#0A0E1A] border border-[#1E2A3F] focus:border-[#3B82F6] focus:ring-1 focus:ring-[#3B82F6] outline-none p-3 text-base text-white font-mono"
          >
            {REGIONS.map((r) => (
              <option key={r.value} value={r.value}>{r.label}</option>
            ))}
          </select>
        </div>

        <div>
          <label className="block label-mono mb-2">Details (optional)</label>
          <textarea
            value={form.message}
            onChange={set("message")}
            rows={5}
            maxLength={2000}
            data-testid="data-request-message-input"
            placeholder="Add any context that helps us locate or process your request"
            className="w-full bg-[#0A0E1A] border border-[#1E2A3F] focus:border-[#3B82F6] focus:ring-1 focus:ring-[#3B82F6] outline-none p-3 text-sm text-white font-mono"
          />
        </div>

        <label className="flex items-start gap-3 p-4 border border-[#1E2A3F] bg-[#0F1525] cursor-pointer hover:border-[#3B82F6]/50">
          <input
            type="checkbox"
            checked={form.consent_confirmed}
            onChange={set("consent_confirmed")}
            data-testid="data-request-consent-checkbox"
            className="mt-0.5 accent-[#3B82F6]"
            required
          />
          <span className="text-sm text-[#94A3B8]">
            I confirm that the email above belongs to me and authorize IQ Log to process
            this request. I understand the response may take up to 30 days and that my
            email and IP will be logged for audit purposes.
          </span>
        </label>

        <div className="flex items-center gap-3">
          <button
            type="submit"
            disabled={submitting}
            data-testid="data-request-submit-button"
            className="inline-flex items-center gap-2 px-6 py-3.5 bg-[#3B82F6] text-black font-mono text-[11px] uppercase tracking-[0.2em] font-bold hover:bg-[#60A5FA] disabled:opacity-60 neon-glow-soft"
          >
            <Send className="h-4 w-4" strokeWidth={2} />
            {submitting ? "Submitting…" : "Submit request"}
          </button>
          <Link
            to="/privacy"
            data-testid="data-request-privacy-link"
            className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#94A3B8] hover:text-[#3B82F6]"
          >
            Privacy policy →
          </Link>
        </div>
      </form>
    </div>
  );
}
